import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Cpu, ChevronRight } from "lucide-react";
import { SEO } from "@/components/shared";
import { fadeInUp, staggerContainer, HERO_IMAGES } from "@/constants";
import { PROPOSAL_LINK } from "./aicoe/data";
import { SideNav } from "./aicoe/SideNav";
import {
  Overview, Impact, Labs, ArchitectureBlock, Curriculum, Community, Benefits, Rollout, Included, Faq
} from "./aicoe/sections";

const HERO_FACTS = [
  { value: "5", label: "Lab tracks: AI, GenAI, AI+IoT, HPC, Security" },
  { value: "40+", label: "Hours of curated content per lab" },
  { value: "₹0", label: "Program fees for training & certification" },
  { value: "1", label: "Partner, from MoU to mentoring" },
];

const Hero = () => (
  <section className="relative overflow-hidden page-hero border-b border-[#2A3648]" style={{ "--hero-img": `url(${HERO_IMAGES.aiCoe})` }}>
    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#C9A962 1px, transparent 1px), linear-gradient(90deg, #C9A962 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
    <div className="container-custom relative z-10 py-20 md:py-28">
      <motion.div initial="initial" animate="animate" variants={staggerContainer} className="max-w-4xl">
        <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#C9A962]/10 border border-[#C9A962]/25 rounded-full px-4 py-1.5 mb-6">
          <Cpu size={14} className="text-[#C9A962]" />
          <span className="text-[#C9A962] text-xs font-semibold uppercase tracking-wider">Intel&reg; Unnati &middot; Authorized Partner</span>
        </motion.div>
        <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.08]" data-testid="coe-hero-title">
          AI Centre of <span className="text-gradient">Excellence</span>
        </motion.h1>
        <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[#A0ABC0] mb-10 max-w-2xl leading-relaxed">
          Intel Unnati data-centric labs in emerging technologies — infrastructure, curriculum and faculty enablement, delivered end-to-end by Qbic Catalyst Partners.
        </motion.p>
        <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
          <Link to={PROPOSAL_LINK} className="btn-primary inline-flex items-center gap-2" data-testid="coe-hero-proposal-cta">
            Request a Proposal <ArrowRight size={18} />
          </Link>
          <a href="#architecture" className="btn-secondary inline-flex items-center gap-2" data-testid="coe-hero-architecture-link">
            View Architecture <ChevronRight size={18} />
          </a>
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        className="hidden lg:grid absolute right-8 xl:right-16 top-1/2 -translate-y-1/2 grid-cols-2 gap-3 w-[380px]"
        data-testid="coe-hero-facts"
      >
        {HERO_FACTS.map((f) => (
          <div key={f.label} className="p-5 rounded-xl bg-[#121E30]/80 backdrop-blur border border-[#2A3648]">
            <p className="text-3xl font-bold text-[#C9A962] mb-1">{f.value}</p>
            <p className="text-[#A0ABC0] text-xs leading-snug">{f.label}</p>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

export default function AICoEPage() {
  return (
    <div className="pt-20">
      <SEO
        title="AI Centre of Excellence | Intel Unnati Labs"
        description="Set up an Intel Unnati AI Centre of Excellence with Qbic Catalyst Partners — AI, GenAI, AI+IoT, HPC and Security labs, curated curriculum, faculty enablement and end-to-end implementation."
        keywords="AI Centre of Excellence, Intel Unnati, AI lab for colleges, HPC lab, GenAI lab, AIIoT lab, faculty development program, Qbic Catalyst Partners"
        path="/ai-centre-of-excellence"
      />
      <Hero />
      <div className="theme-light tint-mist">
      <div className="container-custom">
        <div className="lg:grid lg:grid-cols-[200px_1fr] lg:gap-16">
          <SideNav />
          <div className="min-w-0">
            <Overview />
            <Impact />
            <Labs />
            <ArchitectureBlock />
            <Curriculum />
            <Community />
            <Benefits />
            <Rollout />
            <Included />
            <Faq />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
